import * as _ from 'lodash';
import * as Pappa from 'papaparse'
import * as React from 'react'
import { Alert, Badge, Button, Card, CardBody, CardFooter, Col, Form, FormGroup, Input, InputGroup, Row } from 'reactstrap';
import { HttpRequest } from '../../../util/http';
import { userData } from '../../../util/userData';
import { ContentCardHeader } from '../../ui/ContentCardHeader';
import { DataGrid } from '../../ui/DataGrid';
import { GetAllSegments } from '../../util';
import { ISegment } from '../interfaces';
import { getParentSegments } from '../utils';

interface IUploadSegmentsProps {
  onSuccess?: () => Promise<any> | void
}

interface IUploadSegmentsState {
  numOfDuplicatesWereRemoved: number
  uploadSegments: ISegment[]
  existingSegmentParents: ISegment[]
  success: boolean;
  uploading: boolean;
  file?: File
  uploadError?: string
  csvError?: string;
  hierarchy: string;
}

export class UploadSegments extends React.Component<IUploadSegmentsProps, IUploadSegmentsState> {
  state: IUploadSegmentsState = {
    uploadSegments: [],
    numOfDuplicatesWereRemoved: 0,
    success: false,
    uploading: false,
    hierarchy: '',
    existingSegmentParents: []
  }

  formElem: HTMLFormElement

  componentWillMount() {
    this.loadExistingSegmentParents()
  }

  loadExistingSegmentParents() {
    return GetAllSegments()
      .then((segments) => getParentSegments(segments))
      .then((existingSegmentParents) => this.setState({ existingSegmentParents }));
  }

  reset() {
    const file = undefined //reset
    const uploadSegments = [] //reset
    const numOfDuplicatesWereRemoved = 0
    const uploadError = undefined
    const csvError = undefined
    const hierarchy = ''

    this.setState({ file, uploadSegments, numOfDuplicatesWereRemoved, uploadError, csvError, hierarchy })

    this.loadExistingSegmentParents()

    if (this.formElem) {
      this.formElem.reset()
    }
  }

  updateFile(e: React.ChangeEvent<HTMLInputElement>) {
    const fileReader = new FileReader()
    const inputElem = e.target
    const file = inputElem.files[0]
    const success = undefined
    let uploadSegments: ISegment[] = []
    let csvError

    if (!file) { return }

    this.setState({ file, csvError, uploadSegments, success })

    fileReader.onload = () => {
      const csvParseOptions = {};
      const csvData = String(fileReader.result);
      //const customer = userData.getCurrentProject();
      let resSegmentData: string[][] = [];

      try {
        resSegmentData = Pappa.parse(csvData).data;
      } catch (err) {
        csvError = err.message;
        this.setState({ csvError });
        return;
      }

      //Unique by "segmentValueName_segmentName"
      const uniqueSegmentData = _.uniqBy(resSegmentData, (data) => `${data[0]}_${data[4]}`);
      const numOfDuplicatesWereRemoved = uniqueSegmentData.length - resSegmentData.length;
      const { existingSegmentParents } = this.state
      const findExistingParent = (segmentName) => {
        const segmentNameLower = segmentName.trim().toLowerCase()

        return existingSegmentParents
          .find((esp) => esp.segmentName.trim().toLowerCase() === segmentNameLower)
      }

      uploadSegments = uniqueSegmentData.filter((value, index) => {
        return value.length === 5
      }).map((singleSegment) => {
        const id = 0
        const operator = ''
        const segmentValueName = singleSegment[0]
        const fileColumnName = singleSegment[1]
        const fileValue = singleSegment[2]
        let sortOrder = Number(singleSegment[3])
        let segmentName = singleSegment[4]
        const existingSegmentParent = findExistingParent(segmentName)
        let segmentAvid = ''
        let segmentValueAvid = ''
        let useExisting = false

        if (isNaN(sortOrder)) {
          sortOrder = 0
        }

        if (existingSegmentParent) {
          segmentValueAvid = existingSegmentParent.segmentValueAvid
          segmentName = existingSegmentParent.segmentName
          segmentAvid = existingSegmentParent.segmentAvid
          useExisting = true
        }

        return { segmentAvid, useExisting, segmentValueAvid, operator, id, fileColumnName, fileValue, segmentName, sortOrder, segmentValueName }

      });
      this.setState({ uploadSegments, numOfDuplicatesWereRemoved })
    }

    fileReader.readAsText(file, 'utf-8')
  }

  removeAvid(segment: ISegment) {
    const { uploadSegments } = this.state
    const index = uploadSegments.findIndex((storedSegment) => storedSegment.segmentName === segment.segmentName);

    if (index >= 0) {
      uploadSegments.splice(index, 1)

      this.setState({ uploadSegments })

      if (uploadSegments.length <= 0 && this.formElem) {
        const file = undefined

        this.formElem.reset()

        this.setState({ file })
      }
    }
  }

  fireOnSuccessPromise() {
    const { onSuccess } = this.props
    let onSuccessPromise = onSuccess ? onSuccess() : undefined

    if (!onSuccessPromise) {
      onSuccessPromise = Promise.resolve();
    }

    return onSuccessPromise;
  }

  async uploadSegments(e: React.FormEvent<HTMLFormElement>) {
    const { uploadSegments } = this.state
    const method = 'POST'
    const endpoint = '/scripts/segments/save'

    e.preventDefault()

    this.setState({ uploadError: undefined, uploading: true })

    const uploadErrors: string[] = []

    for (const uploadSegment of uploadSegments) {
      const data = { ...uploadSegment }

      try {
        const res: any = await HttpRequest.fetch(endpoint, { method, data })

        if (typeof res.error === 'string') {
          throw new Error(res.error)
        }
      } catch (e) {
        let newUploadError = `${uploadSegment.segmentValueName} was unable to upload: `;

        if (typeof e === 'string') {
          newUploadError += e;
        } else if (e instanceof Error) {
          newUploadError += e.message;
        } else {
          newUploadError += 'An unknown error occured.'
        }

        uploadErrors.push(newUploadError);
      }
    }

    let uploadError = uploadErrors.join(', ')
    uploadError = uploadError === '' ? undefined : uploadError

    this.setState({ uploading: false, uploadError })

    if (!uploadError) {
      this.fireOnSuccessPromise().then(() => {
        this.setState({ success: true })
        setTimeout(() => this.setState({ success: undefined }), 5 * 1000)

        this.reset()
      })
    }

  }

  render() {
    const { file, uploadSegments, numOfDuplicatesWereRemoved, uploadError, success, uploading, csvError } = this.state
    const fileName = file ? file.name : 'Choose CSV File'
    const duplicatesMoreThanZero = numOfDuplicatesWereRemoved > 0
    const duplicatesMoreThanOne = numOfDuplicatesWereRemoved > 1

    return (
      <Card>
        <Form onSubmit={(e) => this.uploadSegments(e)}
          innerRef={(formElem) => this.formElem = formElem}>
          <ContentCardHeader header="Upload Segments" sticky borderBottom />
          <CardBody>
            {csvError &&
              <FormGroup>
                <Alert color="danger">
                  <i className="fas fa-fw fa-exclamation-triangle" />
                  <span>&nbsp;Unable to parse CSV file.</span>
                  <span>&nbsp;Verify the file is a proper CSV and upload again:</span>
                  <span>&nbsp;{csvError}</span>
                </Alert>
              </FormGroup>
            }
            <Row>
              <Col md={6}>
                <FormGroup>
                  <label>CSV File</label>
                  <InputGroup>
                    <div className="custom-file">
                      <Input type="file" className="custom-file-input"
                        style={{ cursor: 'pointer' }}
                        accept=".csv,text/csv"
                        disabled={uploading || !!uploadError}
                        onChange={(e) => this.updateFile(e)} />
                      <label className="custom-file-label">{fileName}</label>
                    </div>
                  </InputGroup>
                </FormGroup>
              </Col>
              {/* <Col md={6}>
                <FormGroup>
                  <label>Hierarchy Level</label>
                  <HierarchySelect required
                    readOnly={uploading || !!uploadError}
                    onChange={(hierarchy) => this.updateHierarchy(hierarchy)}
                    value={this.state.hierarchy} />
                </FormGroup>
              </Col> */}
            </Row>

            {duplicatesMoreThanZero &&
              <FormGroup>
                <Alert color="warning">
                  <i className="fas fa-fw fa-sticky-note" />
                  <span>&nbsp;Note:&nbsp;{numOfDuplicatesWereRemoved.toLocaleString()}&nbsp;{duplicatesMoreThanOne ? 'duplicates were' : 'duplicate was'}&nbsp;removed.</span>
                </Alert>
              </FormGroup>
            }

            {uploadSegments.length > 0 &&
              <FormGroup>
                <DataGrid<ISegment>
                  records={uploadSegments}
                  columns={[
                    { header: 'New Segment Label', property: 'segmentValueName' },
                    { header: 'File Column', property: 'fileColumnName' },
                    { header: 'File Value', property: 'fileValue' },
                    { header: 'Sort Order', property: 'sortOrder' },
                    {
                      header: 'Parent Label', property: 'segmentName', render: (avid) =>
                        <section>
                          {avid.segmentName}
                          {!avid.useExisting &&
                            <>
                              <span>&nbsp;</span>
                              <Badge color="danger" className="text-white">New Parent</Badge>
                            </>
                          }
                        </section>
                    },
                    {
                      header: 'Remove', width: 100, align: 'center', render: (avid) =>
                        <Button color="link" href="javascript:void(0)"
                          onClick={() => this.removeAvid(avid)}
                          disabled={success || uploading || !!uploadError}>
                          <i className="fas fa-fw fa-trash-alt" />
                        </Button>
                    }
                  ]}
                />
              </FormGroup>
            }
          </CardBody>
          <CardFooter className="d-flex justify-content-end">
            {uploadError &&
              <Alert color="danger" className="alert-input-height mr-2">
                <i className="fas fa-fw fa-exclamation-triangle" />
                <span>&nbsp;{uploadError}</span>
              </Alert>
            }
            {success &&
              <Alert color="success" className="alert-input-height mr-2">
                <i className="fas fa-fw fa-check-square" />
                <span>&nbsp;Segments Successfully Uploaded!</span>
              </Alert>
            }
            {!success && !uploadError &&
              <Button type="submit" color="action" className="mr-2"
                disabled={uploadSegments.length <= 0 || uploading}>
                {uploading ?
                  <>
                    <span>Uploading&nbsp;</span>
                    <i className="fas fa-fw fa-spin fa-cog" />
                  </>
                  :
                  <span>Upload</span>
                }
              </Button>
            }
            {!success &&
              <Button color="white" disabled={uploading} onClick={() => this.reset()}>
                <span>Reset</span>
              </Button>
            }
          </CardFooter>
        </Form>
      </Card >
    )
  }
}
